import { makeAutoObservable, runInAction } from 'mobx'
import { EnumDropdownItemVariant, type IDropdownItem } from 'shared/ui'
import { logger } from 'shared/lib'
import { managerStream } from 'entities/DeviceSettings/store/managerStream'
import { deviceStore, type IDeviceStore } from './deviceStore'
import { DefaultInboxStore } from './defaultInboxStore'

export enum DeviceItemsEnum {
  audioInput = 'audioInput',
  videoInput = 'videoInput',
  audioOutput = 'audioOutput',
  recordAuto = 'recordAuto',
  defaultInbox = 'defaultInbox',
}

type IDeviceSettingsStoreProps = {
  items: Array<DeviceItemsEnum>
}

export class DeviceSettingsStore {
  private _items: IDeviceSettingsStoreProps['items'] = []
  defaultInboxStore: DefaultInboxStore | null = null
  isInboxSelectorOpen = false
  private _loading = false

  constructor({ items }: IDeviceSettingsStoreProps) {
    this._items = items
    if (items.includes(DeviceItemsEnum.defaultInbox)) {
      this.defaultInboxStore = new DefaultInboxStore()
    }
    makeAutoObservable(this)
  }

  get stream() {
    return managerStream.streamDefault
  }

  get loading() {
    return this._loading
  }

  get items() {
    return this._items
  }

  openInboxSelector = () => {
    this.isInboxSelectorOpen = true
  }

  closeInboxSelector = () => {
    this.isInboxSelectorOpen = false
  }

  get device(): IDeviceStore {
    return deviceStore
  }

  get audioOutputsItems(): IDropdownItem[] {
    return this.device.audioOutputs.map((device) => ({
      id: device.deviceId,
      label: device.label,
      activeValue: this.device.audioOutputId === device.deviceId,
      variant: EnumDropdownItemVariant.CheckedRight,
    }))
  }

  get audioInputsItems(): IDropdownItem[] {
    return this.device.audioInputs.map((device) => ({
      id: device.deviceId,
      label: device.label,
      activeValue: this.device.audioInputId === device.deviceId,
      variant: EnumDropdownItemVariant.CheckedRight,
    }))
  }

  get videoInputsItems(): IDropdownItem[] {
    return this.device.videoInputs.map((device) => ({
      id: device.deviceId,
      label: device.label,
      activeValue: this.device.videoInputId === device.deviceId,
      variant: EnumDropdownItemVariant.CheckedRight,
    }))
  }

  initStream = async () => {
    try {
      this.removeStream()

      const isAudio =
        this._items.includes(DeviceItemsEnum.audioOutput) ||
        this._items.includes(DeviceItemsEnum.audioInput)
      const isVideo = this._items.includes(DeviceItemsEnum.videoInput)

      const videoInput = this.device.videoInput
      const videoOptions = {
        video: {
          deviceId: {
            exact: videoInput?.deviceId,
          },
          groupId: {
            exact: videoInput?.groupId,
          },
          aspectRatio: 4 / 3,
        },
      }

      const audioInput = this.device.audioInput
      const audioOptions = {
        audio: {
          deviceId: {
            exact: audioInput?.deviceId,
          },
          groupId: {
            exact: audioInput?.groupId,
          },
        },
      }

      const stream = await window.navigator.mediaDevices.getUserMedia({
        ...(isVideo ? videoOptions : null),
        ...(isAudio ? audioOptions : null),
      })

      this.addStream(stream)
    } catch (e) {
      logger.error(e)
    }
  }

  syncDevicesStream = async () => {
    try {
      runInAction(() => {
        this._loading = true
      })

      await this.device.syncDevices()
      await this.initStream()
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  setSelectedAudioOutput = (deviceId: string) => {
    this.device.setAudioOutput(deviceId)
    this.initStream()
  }

  setSelectedAudioInput = (deviceId: string) => {
    this.device.setAudioInput(deviceId)
    this.initStream()
  }

  setSelectedVideoInput = (deviceId: string) => {
    this.device.setVideoInput(deviceId)
    this.initStream()
  }

  removeStream = () => {
    managerStream.removeStreams('default')
  }

  addStream = (stream: MediaStream) => {
    managerStream.addStream('default', stream)
  }

  checkPermissions = async (isAudio: boolean) => {
    try {
      const config: MediaStreamConstraints = {}

      if (isAudio) config.audio = true
      if (!isAudio) config.video = true

      const stream = await window.navigator.mediaDevices.getUserMedia(config)

      this.addStream(stream)

      return true
    } catch (e) {
      logger.error(e)

      return false
    }
  }

  dispose = () => {
    this.removeStream()
  }
}
